import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/AuthContext";
import { useT } from "../lib/i18n";

function AuthShell({ eyebrow, title, subtitle, children, footer }) {
  return (
    <main className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark" data-testid="auth-shell">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between line-b line-dark">
        <Link to="/" className="font-logo text-2xl md:text-3xl accent-hover" data-testid="auth-logo">JOH.</Link>
        <Link to="/" className="chip text-[#A3A3A3] accent-hover inline-flex items-center gap-1" data-testid="auth-back-home">
          <ArrowLeft size={14}/> Terug
        </Link>
      </header>
      <section className="min-h-[calc(100vh-73px)] flex items-center px-6 md:px-10 py-16">
        <div className="max-w-lg w-full mx-auto">
          <div className="chip text-[#A3A3A3]">— {eyebrow}</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-display uppercase text-5xl md:text-7xl leading-[0.88] mt-3"
            data-testid="auth-title"
          >
            {title}
          </motion.h1>
          {subtitle && <p className="mt-4 text-[#DDD6CB] text-base md:text-lg">{subtitle}</p>}
          <div className="mt-10 border border-[#F5F3F0]/20 p-6 md:p-8 bg-[#111]/60 backdrop-blur-sm">{children}</div>
          {footer && <div className="mt-6 text-center text-[#A3A3A3] text-sm">{footer}</div>}
        </div>
      </section>
    </main>
  );
}

function TextField({ label, testid, type = "text", value, onChange, required, autoComplete, placeholder }) {
  const [showPw, setShowPw] = useState(false);
  const isPw = type === "password";
  return (
    <div>
      <label className="chip text-[#A3A3A3]">{label}</label>
      <div className="relative">
        <input
          required={required}
          type={isPw ? (showPw ? "text" : "password") : type}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className="editorial-input dark pr-10"
          data-testid={testid}
        />
        {isPw && (
          <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#F5F3F0]" data-testid={`${testid}-toggle`} tabIndex={-1}>
            {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------- GOOGLE LOGIN (Emergent-managed) ---------------- */
function GoogleButton({ label, testid }) {
  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={startGoogleLogin}
      className="w-full border border-[#F5F3F0]/30 py-3.5 px-4 flex items-center justify-center gap-3 chip text-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-colors duration-300"
      data-testid={testid}
    >
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
      </svg>
      {label}
    </button>
  );
}

function OrDivider({ label = "of" }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="h-px flex-1 bg-[#F5F3F0]/15" />
      <span className="chip text-[#A3A3A3]">{label}</span>
      <div className="h-px flex-1 bg-[#F5F3F0]/15" />
    </div>
  );
}

/* ---------------- LOGIN ---------------- */
export function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { signIn, user } = useAuth();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  // Al ingelogd? Direct door (ook bij browser "terug"). Admins/owners → /admin, members → /dashboard.
  useEffect(() => {
    if (user && user !== false) nav(params.get("next") || (["admin", "owner"].includes(user?.role) ? "/admin" : "/dashboard"), { replace: true });
  }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await signIn(email.trim(), password, remember);
      toast.success("Welkom terug");
      const redirect = params.get("next") || (["admin", "owner"].includes(u?.role) ? "/admin" : "/dashboard");
      nav(redirect, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 428) {
        toast.info("Stel eerst een wachtwoord in voor dit account.");
        nav(`/wachtwoord-instellen?email=${encodeURIComponent(email.trim())}`);
      } else {
        toast.error(formatApiError(err?.response?.data?.detail) || "Inloggen mislukt");
      }
    }
    setBusy(false);
  };

  return (
    <AuthShell
      eyebrow="Log in"
      title={<>Welkom <span className="font-serif-italic normal-case tracking-tight accent-ink">terug</span>.</>}
      subtitle="Log in om je credits, boekingen en membership te beheren."
      footer={<>Nog geen account? <Link to="/registreren" className="accent-hover underline underline-offset-2" data-testid="login-to-register">Registreer</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <GoogleButton label="Doorgaan met Google" testid="login-google-button" />
        <OrDivider label="of met e-mail" />
        <TextField label="E-mail" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} testid="login-email"/>
        <TextField label="Wachtwoord" type="password" required autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} testid="login-password"/>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer text-[#DDD6CB]" data-testid="login-remember-wrap">
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} data-testid="login-remember"/>
            Onthoud mij
          </label>
          <Link to="/wachtwoord-vergeten" className="chip text-[#A3A3A3] accent-hover" data-testid="login-forgot">Wachtwoord vergeten?</Link>
        </div>
        <button type="submit" disabled={busy} className="btn-light w-full justify-center" data-testid="login-submit">
          {busy ? "…" : (<>Log in <ArrowUpRight size={14}/></>)}
        </button>
      </form>
    </AuthShell>
  );
}

/* ---------------- REGISTER ---------------- */
const GENDER_OPTIONS = [["male", "Man"], ["female", "Vrouw"], ["undisclosed", "Zeg ik liever niet"]];
const RELATION_OPTIONS = [["partner", "Partner"], ["ouder", "Ouder"], ["broer_zus", "Broer / zus"], ["vriend_in", "Vriend / vriendin"], ["familie", "Familie"], ["anders", "Anders"]];

export function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [track, setTrack] = useState(null); // null = keuzescherm | "groep" | "pt"
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", accept_terms: false,
    dob: "", gender: "", ec_name: "", ec_phone: "", ec_relation: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) { toast.error("Accepteer de algemene voorwaarden"); return; }
    if (form.password.length < 8) { toast.error("Wachtwoord moet minimaal 8 tekens zijn"); return; }
    if (!form.dob || !form.gender || !form.ec_name.trim() || !form.ec_phone.trim() || !form.ec_relation) {
      toast.error("Vul alle verplichte velden in (ook geboortedatum, geslacht en noodcontact)"); return;
    }
    setBusy(true);
    try {
      await register({
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        password: form.password, accept_terms: form.accept_terms,
        dob: form.dob, gender: form.gender,
        emergency_contact: { name: form.ec_name, phone: form.ec_phone, relation: form.ec_relation },
      });
      toast.success("Account aangemaakt");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Registreren mislukt");
    }
    setBusy(false);
  };

  if (track === null) {
    return (
      <AuthShell
        eyebrow="Maak een account"
        title={<>Kies je <span className="font-serif-italic normal-case tracking-tight accent-ink">traject</span>.</>}
        subtitle="Groepslessen in de club, of persoonlijke 1-op-1 training met een eigen PT-omgeving."
        footer={<>Heb je al een account? <Link to="/login" className="accent-hover underline underline-offset-2" data-testid="register-to-login">Log in</Link></>}
      >
        <div className="space-y-4" data-testid="register-choice">
          <button onClick={() => setTrack("groep")} className="w-full text-left border border-[#F5F3F0]/25 p-5 hover:border-[#F5F3F0] transition-colors group" data-testid="register-choice-group">
            <div className="chip text-[#A3A3A3]">GROEPSLESSEN</div>
            <div className="font-display uppercase text-2xl mt-1 group-hover:accent-ink">Trainen in de club</div>
            <p className="text-[#A3A3A3] text-sm mt-1">Wekelijks rooster, memberships en strippenkaarten.</p>
          </button>
          <button onClick={() => nav("/pt")} className="w-full text-left border p-5 transition-colors group" style={{ borderColor: "var(--joh-accent)" }} data-testid="register-choice-pt">
            <div className="chip" style={{ color: "var(--joh-accent)" }}>PERSONAL TRAINING</div>
            <div className="font-display uppercase text-2xl mt-1 group-hover:accent-ink">1-op-1 met een coach</div>
            <p className="text-[#A3A3A3] text-sm mt-1">Eigen traject, intake en persoonlijk PT-dashboard.</p>
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow={<>Maak een account · <button onClick={() => setTrack(null)} className="accent-hover underline underline-offset-2" data-testid="register-back-choice">Groepslessen</button></>}
      title={<>Word <span className="font-serif-italic normal-case tracking-tight accent-ink">member</span>.</>}
      subtitle="Ontvang een dashboard met je credits, boekingen en membership status."
      footer={<>Heb je al een account? <Link to="/login" className="accent-hover underline underline-offset-2" data-testid="register-to-login">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <GoogleButton label="Registreren met Google" testid="register-google-button" />
        <OrDivider label="of met e-mail" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Voornaam" required autoComplete="given-name" value={form.first_name} onChange={set("first_name")} testid="register-first-name"/>
          <TextField label="Achternaam" required autoComplete="family-name" value={form.last_name} onChange={set("last_name")} testid="register-last-name"/>
        </div>
        <TextField label="E-mail" type="email" required autoComplete="email" value={form.email} onChange={set("email")} testid="register-email"/>
        <TextField label="Wachtwoord (min. 8 tekens)" type="password" required autoComplete="new-password" value={form.password} onChange={set("password")} testid="register-password"/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Geboortedatum" type="date" required value={form.dob} onChange={set("dob")} testid="register-dob"/>
          <div>
            <label className="chip text-[#A3A3A3]">Geslacht</label>
            <select required value={form.gender} onChange={set("gender")} className="editorial-input dark" data-testid="register-gender">
              <option value="" className="bg-[#0A0A0A]">Kies…</option>
              {GENDER_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0A0A0A]">{l}</option>)}
            </select>
          </div>
        </div>
        <div className="border border-[#F5F3F0]/15 p-4 space-y-4" data-testid="register-emergency-block">
          <div className="chip text-[#A3A3A3]">— Noodcontact (verplicht)</div>
          <TextField label="Naam noodcontact" required value={form.ec_name} onChange={set("ec_name")} testid="register-ec-name"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Telefoon noodcontact" type="tel" required autoComplete="tel" placeholder="+31 6 12345678" value={form.ec_phone} onChange={set("ec_phone")} testid="register-ec-phone"/>
            <div>
              <label className="chip text-[#A3A3A3]">Relatie</label>
              <select required value={form.ec_relation} onChange={set("ec_relation")} className="editorial-input dark" data-testid="register-ec-relation">
                <option value="" className="bg-[#0A0A0A]">Kies…</option>
                {RELATION_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0A0A0A]">{l}</option>)}
              </select>
            </div>
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer text-[#DDD6CB] text-sm" data-testid="register-terms-wrap">
          <input type="checkbox" checked={form.accept_terms} onChange={set("accept_terms")} className="mt-1" data-testid="register-accept-terms"/>
          <span>
            Ik ga akkoord met de{" "}
            <a href="/voorwaarden" target="_blank" rel="noreferrer" className="accent-ink underline underline-offset-2" style={{color: "var(--joh-accent)"}}>algemene voorwaarden</a>
            {" "}en de{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="accent-ink underline underline-offset-2" style={{color: "var(--joh-accent)"}}>privacyverklaring</a>.
          </span>
        </label>
        <button type="submit" disabled={busy} className="btn-light w-full justify-center" data-testid="register-submit">
          {busy ? "…" : (<>Maak account <ArrowUpRight size={14}/></>)}
        </button>
      </form>
    </AuthShell>
  );
}

/* ---------------- FORGOT PASSWORD ---------------- */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error("Er ging iets mis");
    }
    setBusy(false);
  };

  return (
    <AuthShell
      eyebrow="Wachtwoord vergeten"
      title={<>Reset je <span className="font-serif-italic normal-case tracking-tight accent-ink">wachtwoord</span>.</>}
      subtitle="We sturen je een link waarmee je een nieuw wachtwoord kunt instellen."
      footer={<Link to="/login" className="accent-hover underline underline-offset-2" data-testid="forgot-to-login">← Terug naar login</Link>}
    >
      {sent ? (
        <div className="text-center py-6" data-testid="forgot-success">
          <div className="w-14 h-14 mx-auto rounded-full border border-[#F5F3F0] flex items-center justify-center mb-4" style={{ background: "var(--joh-accent)", borderColor: "var(--joh-accent)", color: "#0A0A0A" }}>
            <Check size={22}/>
          </div>
          <p className="text-[#DDD6CB]">Als dit adres bekend is bij ons, ontvang je binnen enkele minuten een e-mail met een reset-link (1 uur geldig).</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <TextField label="E-mail" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} testid="forgot-email"/>
          <button type="submit" disabled={busy} className="btn-light w-full justify-center" data-testid="forgot-submit">
            {busy ? "…" : (<>Verstuur reset-link <ArrowUpRight size={14}/></>)}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

/* ---------------- RESET PASSWORD (via email link) ---------------- */
export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Wachtwoorden komen niet overeen"); return; }
    if (pw.length < 8) { toast.error("Wachtwoord moet minimaal 8 tekens zijn"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Wachtwoord opgeslagen. Log in.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reset mislukt");
    }
    setBusy(false);
  };

  if (!token) return <AuthShell eyebrow="Reset" title="Ongeldige link."><p className="text-[#DDD6CB]">De reset-link is ongeldig of onvolledig.</p></AuthShell>;

  return (
    <AuthShell eyebrow="Nieuw wachtwoord" title={<>Kies een <span className="font-serif-italic normal-case tracking-tight accent-ink">nieuw</span> wachtwoord.</>}>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <TextField label="Nieuw wachtwoord (min. 8 tekens)" type="password" required autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} testid="reset-password"/>
        <TextField label="Herhaal wachtwoord" type="password" required autoComplete="new-password" value={pw2} onChange={e => setPw2(e.target.value)} testid="reset-password-2"/>
        <button type="submit" disabled={busy} className="btn-light w-full justify-center" data-testid="reset-submit">
          {busy ? "…" : (<>Sla op <ArrowUpRight size={14}/></>)}
        </button>
      </form>
    </AuthShell>
  );
}

/* ---------------- SET PASSWORD (for legacy email-only users) ---------------- */
export function SetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setPassword } = useAuth();
  const [email, setEmail] = useState(params.get("email") || "");
  const [pw, setPw] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Wachtwoord moet minimaal 8 tekens zijn"); return; }
    setBusy(true);
    try {
      await setPassword({ email: email.trim(), password: pw, first_name: firstName || null, last_name: lastName || null });
      toast.success("Account geactiveerd");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Instellen mislukt");
    }
    setBusy(false);
  };

  return (
    <AuthShell
      eyebrow="Eerste keer inloggen"
      title={<>Activeer je <span className="font-serif-italic normal-case tracking-tight accent-ink">account</span>.</>}
      subtitle="We herkennen je e-mailadres van eerdere boekingen. Kies een wachtwoord om je account te activeren."
    >
      <form onSubmit={submit} className="space-y-5" data-testid="setpw-form">
        <TextField label="E-mail" type="email" required value={email} onChange={e => setEmail(e.target.value)} testid="setpw-email"/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Voornaam (optioneel)" value={firstName} onChange={e => setFirstName(e.target.value)} testid="setpw-first-name"/>
          <TextField label="Achternaam (optioneel)" value={lastName} onChange={e => setLastName(e.target.value)} testid="setpw-last-name"/>
        </div>
        <TextField label="Wachtwoord (min. 8 tekens)" type="password" required autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} testid="setpw-password"/>
        <button type="submit" disabled={busy} className="btn-light w-full justify-center" data-testid="setpw-submit">
          {busy ? "…" : (<>Activeer account <ArrowUpRight size={14}/></>)}
        </button>
      </form>
    </AuthShell>
  );
}
